/**
 * cave_light_hook — THE ONE HOOK the global ambient takes inside a cave (shader gaps program, agent A, 2026-09-29).
 *
 * While the player is inside a cave (`atlas.caveId`), the dynamic PBR receivers — the player's own body, versonas,
 * props — have no baked field of their own: they are lit by the GLOBAL hemisphere + sky IBL, i.e. by the whole sky as
 * if they stood in a field. The cave module samples the baked field at the player (`hookTargetAt`: the sky transfer
 * `mul` and the crystals' local irradiance `tint`) and sets this hook's TARGET; `stepCaveHook` eases toward it with a
 * 0.5 s time constant (the auto-exposure meter never sees a step). Outside every cave the target is the IDENTITY
 * (mul 1, no tint). Two writers apply it, both inside LightingModule — it stays the only writer of the hemisphere:
 *
 *   · the HEMISPHERE (applyTimeOfDay, after it sets the sky/ground colours):  sky' = sky·m + t/I,  ground' = ground·m + t/I
 *     — so its irradiance becomes m·E + t for every normal (HemisphereLightNode: mix(ground, sky, w) × I).
 *   · the IBL (a LightingModule system at RenderSub/8, after SkyModule's carrier writes `environmentIntensity` at 7):
 *     env' = env·m. The carrier rewrites the value every frame; if it skipped a frame, the hook divides its own
 *     previous factor back out first — the multiply never compounds.
 *
 * The cave's OWN materials (cave_light_material.ts) undo the hook exactly — `(E_hemi − t)/m` and `env/m` — before
 * applying their per-fragment baked sky transfer, so the rock is never dimmed twice: the uniforms below carry the
 * values the hook APPLIED this frame. `CAVE_HOOK_MUL_FLOOR` keeps m above zero so that division stays exact.
 *
 * Levers: `caveHookState` (read by `window.__caveLight()`).
 */
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';

/** The smallest sky multiplier the hook applies (deep in a cave a body keeps ½ % of the sky; the rock's undo stays exact in
 *  f32 — (m·E + t − t)/m loses ~1e-7 relative; 3 % made an actor glow beside black rock at an adapted exposure). */
export const CAVE_HOOK_MUL_FLOOR = 0.005;
/** Easing time constant (s). */
export const CAVE_HOOK_TAU_S = 0.5;

export const caveHookState = {
  targetMul: 1, targetTint: [0, 0, 0] as [number, number, number],
  mul: 1, tint: [0, 0, 0] as [number, number, number],
  /** what the hook wrote this frame (the materials' undo reads the uniforms, this is for the lever) */
  applied: { hemi: false, env: false, envBase: 1, envWritten: 1 },
  enabled: true,
};

/** What the hook APPLIED this frame, per writer (the cave materials' undo reads these — each set is written where it
 *  is applied, so the undo is exact even while the hook eases between the two writers' systems). */
export const uCaveHemiMul = uniform(1);
export const uCaveHemiTint = uniform(new THREE.Color(0, 0, 0));
export const uCaveEnvMul = uniform(1);

/** Set the hook's target — the cave module every tick; `null` = the identity (outside every cave). */
export function setCaveHookTarget(t: { mul: number; tint: readonly [number, number, number] } | null): void {
  if (!t || !caveHookState.enabled) { caveHookState.targetMul = 1; caveHookState.targetTint = [0, 0, 0]; return; }
  caveHookState.targetMul = Math.min(1, Math.max(CAVE_HOOK_MUL_FLOOR, t.mul));
  caveHookState.targetTint = [Math.max(0, t.tint[0]), Math.max(0, t.tint[1]), Math.max(0, t.tint[2])];
}

/** Ease toward the target (exponential, frame-rate independent). */
export function stepCaveHook(dt: number): void {
  const k = 1 - Math.exp(-Math.max(0, Math.min(1, dt)) / CAVE_HOOK_TAU_S);
  const s = caveHookState;
  s.mul += (s.targetMul - s.mul) * k;
  for (let i = 0; i < 3; i++) s.tint[i] += (s.targetTint[i] - s.tint[i]) * k;
  if (Math.abs(s.mul - s.targetMul) < 1e-4) s.mul = s.targetMul;
  // an exponential never ARRIVES: snap the last invisible sliver, or the hook never goes idle (the world probe caught a
  // 4e-7 tint keeping the hemisphere hook applied at the porch)
  for (let i = 0; i < 3; i++) if (Math.abs(s.tint[i] - s.targetTint[i]) < 2e-5) s.tint[i] = s.targetTint[i];
}

/** Is the hook the identity right now? */
export function caveHookIdle(): boolean {
  const s = caveHookState;
  return s.mul === 1 && s.tint[0] === 0 && s.tint[1] === 0 && s.tint[2] === 0 && s.targetMul === 1;
}

/** LightingModule.applyTimeOfDay, after it wrote the hemisphere: m·E + t. */
export function applyCaveHookToHemisphere(h: THREE.HemisphereLight): void {
  const s = caveHookState;
  s.applied.hemi = !caveHookIdle();
  const um = uCaveHemiMul as unknown as { value: number }, ut = uCaveHemiTint as unknown as { value: THREE.Color };
  if (!s.applied.hemi) { um.value = 1; ut.value.setRGB(0, 0, 0); return; }
  const m = Math.max(CAVE_HOOK_MUL_FLOOR, s.mul);
  um.value = m; ut.value.setRGB(s.tint[0], s.tint[1], s.tint[2]);
  const I = Math.max(1e-9, h.intensity);
  h.color.multiplyScalar(m); h.groundColor.multiplyScalar(m);
  h.color.r += s.tint[0] / I; h.color.g += s.tint[1] / I; h.color.b += s.tint[2] / I;
  h.groundColor.r += s.tint[0] / I; h.groundColor.g += s.tint[1] / I; h.groundColor.b += s.tint[2] / I;
}

/** LightingModule's RenderSub/8 system: ease, then scale the sky IBL the carrier wrote at /7. */
export function applyCaveHookToEnv(scene: { environmentIntensity: number }, dt: number): void {
  stepCaveHook(dt);
  const s = caveHookState, a = s.applied;
  const cur = scene.environmentIntensity;
  // the carrier skipped this frame when the value is still what WE wrote: divide our own factor back out first
  const base = a.env && cur === a.envWritten ? a.envBase : cur;
  const m = caveHookIdle() ? 1 : Math.max(CAVE_HOOK_MUL_FLOOR, s.mul);
  const w = base * m;
  if (w !== cur) scene.environmentIntensity = w;
  a.env = m !== 1; a.envBase = base; a.envWritten = w;
  (uCaveEnvMul as unknown as { value: number }).value = m;
}
